import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { OptionalAdditionalStyle } from '../../components.utils';
import { ASPECT_RATIO_LAYOUT, AspectRatio, BaseStyleConfiguration, FULL_HEIGHT_LAYOUT, BaseStyleTypes } from './image.types';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigThemeKey = 'imageWrapper';

interface ImageWrapperDataProps {
  aspectRatio: AspectRatio
  dataId?: string
  baseStyleConfiguration?: BaseStyleConfiguration
}

interface ImageWrapperThemeProps {
  backgroundColor: string
}

const defaultBaseStyleConfiguration = {
  LARGE: ASPECT_RATIO_LAYOUT,
  MEDIUM: ASPECT_RATIO_LAYOUT,
  SMALL: ASPECT_RATIO_LAYOUT,
};

export const getBaseStyle = (aspectRatio: AspectRatio, backgroundColor: string, baseStyleConfiguration: BaseStyleConfiguration) => {
  const getBaseStyleByType = (type:BaseStyleTypes) => {
    switch (type) {
      case FULL_HEIGHT_LAYOUT:
        return {
          paddingTop: 'unset',
          height: '100%',
        };
      case ASPECT_RATIO_LAYOUT:
      default:
        return {
          paddingTop: `calc(100% / (${aspectRatio.x / aspectRatio.y}))`,
        };
    }
  };
  return StyleSheet.create({
    base: {
      backgroundColor,
      position: 'relative',
      overflow: 'hidden',
      margin: 0,
      boxSizing: 'border-box',
      flex: '1 1 100%',
      minWidth: '0%',
      [MEDIA_BREAKPOINTS.LARGE]: {
        ...getBaseStyleByType(baseStyleConfiguration.LARGE),
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        ...getBaseStyleByType(baseStyleConfiguration.MEDIUM),
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        ...getBaseStyleByType(baseStyleConfiguration.SMALL),
      },
    },
  });
};

type ImageWrapperProps = ImageWrapperDataProps & OptionalAdditionalStyle;

export const ImageWrapperComponent: React.FunctionComponent<ImageWrapperProps & ImageWrapperThemeProps> = ({ additionalStyle, aspectRatio, backgroundColor, dataId, children, baseStyleConfiguration = defaultBaseStyleConfiguration }) => {
  const baseStyle = getBaseStyle(aspectRatio, backgroundColor, baseStyleConfiguration);
  return additionalStyle
    ? (
      <figure data-id={dataId} className={css(additionalStyle.style)}>
        <div className={css(baseStyle.base)}>{children}</div>
      </figure>
    )
    : (
      <figure data-id={dataId} className={css(baseStyle.base)}>
        {children}
      </figure>
    );
};

const mapThemeProps = (theme: ImageWrapperThemeProps) => {
  return {
    backgroundColor: theme.backgroundColor,
  };
};

export const ImageWrapper: React.FC<ImageWrapperProps> = props => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><ImageWrapperComponent {...themeContext} {...props} /></ErrorBoundary>;
};
